import { get, some, chain, mapValues, snakeCase, includes, size, forEach, toUpper } from "lodash"
import { Model } from "resources/models"
import { Portfolio } from "resources/portfolios"
import { IsoDate } from "helpers/types/dates"
import { getStartDate, computeStartDate, Period } from "helpers/dates"

export function getMostUsedOrDefaultModelId(portfolios: Portfolio[], models: Model[]): string | null {
  if (portfolios && portfolios.length > 0) {
    const mostUsedModelId = chain(portfolios)
      .countBy(portfolio => portfolio.modelId)
      .toPairs()
      .orderBy(count => count[1], "desc")
      .first()
      .first()
      .value() as string

    // If the user ever loses access to the most used model
    if (some(models, model => model.id === mostUsedModelId)) {
      return mostUsedModelId
    }
  }

  return get(models, "0.id", null)
}

// Shared with modules/model-selector/selectors — single source of truth for the coverage-gate
// math so Portfolio Context, Composite Asset, and the Dashboard can't drift from each other.

// Shared verbatim with modules/model-selector (old) and resources/model-coverage (dashboard) so a
// query-shape change can't silently diverge between the two "modelsAvailability" cache readers.
export const MODELS_AVAILABILITY_QUERY = "query { models { id, name, availability { currentDate, factorsStartDate, securitiesStartDate } } }"

export type CoverageResourceType = "portfolio" | "swap" | "etf" | "index" | "security"

const COVERAGE_RESOURCE_TYPES: CoverageResourceType[] = ["portfolio", "swap", "etf", "index", "security"]

export function normalizeCoverageResourceType(rawType: string | null | undefined): CoverageResourceType | null {
  if (!rawType) {
    return null
  }
  const lowered = rawType.toLowerCase()
  const normalized = lowered === "basket" ? "swap" : lowered
  return includes(COVERAGE_RESOURCE_TYPES, normalized) ? (normalized as CoverageResourceType) : null
}

export interface ModelAvailability {
  id: string
  availability?: { currentDate?: IsoDate | null } | null
}

export interface CoverageModelDate {
  id: string
  from: IsoDate
  to: IsoDate
}

// Single-date membership check (no lookback window) — used for resource types where the coverage
// gate only needs to know "does this model cover the asset today", not a historical range.
export function getCoverageModelsDates(models: ModelAvailability[]): CoverageModelDate[] {
  return chain(models)
    .filter(model => Boolean(get(model, "availability.currentDate")))
    .map(model => {
      const currentDate = model.availability?.currentDate as IsoDate
      return { id: model.id, from: currentDate, to: currentDate }
    })
    .value()
}

function getPeriod(models: unknown[]): Period {
  const periodSelector = Math.floor(size(models) / 5)
  switch (periodSelector) {
    case 0:
      return "1Y"
    case 1:
      return "6M"
    case 2:
    case 3:
      return "3M"
    default:
      return "1M"
  }
}

export interface WindowedCoverageBounds {
  earliestUploadedDate: IsoDate | null | undefined
  latestUploadedDate: IsoDate | null | undefined
  // Whether the resource being checked is the live/current position set, vs. a historical
  // snapshot — a non-live check clamps the end date to latestUploadedDate.
  live: boolean
  // Portfolio requires asset availability bounds to compute a window (matching its pre-existing
  // behavior in modules/model-selector/selectors) — no bounds means no coverage dates, not a
  // best-effort guess. Swap never had that requirement: it always computed a window from each
  // model's own current date, falling back to no clamp when bounds are absent. Defaults to true
  // (portfolio's stricter behavior); pass false for swap to preserve its original leniency.
  requireBounds?: boolean
}

// Position sets (portfolio/swap) keep a windowed lookback rather than a single-date check, since
// their composition can drift across the window in a way a single date wouldn't catch.
export function getWindowedCoverageModelsDates(models: ModelAvailability[], { earliestUploadedDate, latestUploadedDate, live, requireBounds = true }: WindowedCoverageBounds): CoverageModelDate[] {
  if (requireBounds && (!earliestUploadedDate || !latestUploadedDate)) {
    return []
  }

  const datedModels = chain(models).filter(model => Boolean(get(model, "availability.currentDate"))).value()
  const period = getPeriod(datedModels)

  return datedModels.map(model => {
    let endDate = model.availability?.currentDate as IsoDate
    if (!live && latestUploadedDate && latestUploadedDate < endDate) {
      endDate = latestUploadedDate
    }
    const startDate = getStartDate(endDate, period) as IsoDate
    return {
      id: model.id,
      from: computeStartDate(earliestUploadedDate ?? startDate, startDate),
      to: endDate,
    }
  })
}

export function normalizeCoverageMap(rawCoverageData: unknown, resourceType: CoverageResourceType): Record<string, boolean> | null {
  const data = (rawCoverageData ?? {}) as Record<string, unknown>
  switch (resourceType) {
    case "portfolio":
    case "swap":
    case "etf":
    case "index":
      return mapValues(data, Boolean)
    case "security":
      return mapValues(data, (model) => !!get(model, "security.descriptors.modelProviderId"))
    default:
      return null
  }
}

export function mergeModelsWithCoverage<M extends { id: string, name: string }>(
  models: M[],
  coverageMap: Record<string, boolean> | null,
  selectedModelId: string | null,
  noRestrictionModels: string[] = [],
): (M & { hasCoverage: boolean })[] {
  const coveredKeys = new Set<string>()
  forEach(coverageMap, (coverageValue, key) => {
    if (coverageValue) {
      coveredKeys.add(key)
    }
  })

  return chain(models)
    .map((model) => ({
      ...model,
      hasCoverage: selectedModelId === model.id
        || includes(noRestrictionModels, model.id)
        || coveredKeys.has(model.id)
        || coveredKeys.has(snakeCase(model.id)),
    }))
    .orderBy(["hasCoverage", "name"], ["desc", "asc"])
    .value()
}

export function generateCoverageQuery(modelsDates: CoverageModelDate[]): string {
  let query = "query($ticker: String, $mic: String, $internalId: String) {"
  forEach(modelsDates, modelDates => {
    query += `${snakeCase(modelDates.id)}: model(id: "${modelDates.id}"){
      security(ticker:$ticker, mic:$mic, internalId: $internalId) {
        descriptors(on:"${modelDates.to}") {
          modelProviderId
        }
      }
    }`
  })
  query += "}"
  return query
}

// The portfolio/swap windowed dates can shift (e.g. once portfolioLive resolves) without
// resourceType/resourceId changing. useResource only refetches when this id changes, so the
// dates must be part of it or a stale coverage response for the old window keeps rendering.
function coverageDatesSignature(modelsDates: CoverageModelDate[]): string {
  return modelsDates.map(modelDates => `${modelDates.id}:${modelDates.from}:${modelDates.to}`).join(",")
}

export type ModelsCoverageRequest =
  | { id: string, query: string, variables?: { ticker?: string, mic?: string, internalId?: string } }
  | { id: string, url: string, payload: { positionSet: { type: string, id: string }, models: CoverageModelDate[] } }

export function buildModelsCoverageRequest({ modelsDates, resourceType, resourceId, account }: {
  modelsDates: CoverageModelDate[]
  resourceType: string
  resourceId: string | { ticker?: string, mic?: string, internalId?: string } | null
  account: string
}): ModelsCoverageRequest | null {
  if (!size(modelsDates) || !resourceId || !resourceType) {
    return null
  }
  const datesSignature = coverageDatesSignature(modelsDates)

  // security uses a cheap single-date descriptors lookup (GraphQL); every other type
  // (portfolio/swap/etf/index) goes through the no-rollover REST coverage gate.
  if (resourceType === "security") {
    return {
      id: `modelsCoverage-${resourceType}${resourceId}-${datesSignature}`,
      query: generateCoverageQuery(modelsDates),
      variables: {
        ticker: get(resourceId, "ticker"),
        mic: get(resourceId, "mic"),
        internalId: get(resourceId, "internalId"),
      },
    }
  }

  const resolvedId = typeof resourceId === "string"
    ? resourceId
    : (resourceType === "etf" ? `${resourceId.ticker}.${resourceId.mic}` : resourceId.ticker ?? "")
  return {
    id: `modelsCoverage-${resourceType}${resolvedId}-${datesSignature}`,
    url: `/api/${account}/coverageGate?positionSet=${encodeURIComponent(resolvedId)}`,
    payload: {
      positionSet: { type: toUpper(resourceType), id: resolvedId },
      models: modelsDates,
    },
  }
}
